import { useCallback, useEffect, useState } from "react";
import { Sidebar } from "./components/Sidebar";
import { Topbar } from "./components/Topbar";
import { Login } from "./screens/Login";
import { Dashboard } from "./screens/Dashboard";
import { Subscriptions } from "./screens/Subscriptions";
import { SubscriptionDetail } from "./screens/SubscriptionDetail";
import { Catalog } from "./screens/Catalog";
import { Users } from "./screens/Users";
import { TweaksPanel, usePalette } from "./TweaksPanel";
import { api, ApiError } from "./lib/api";
import type { CrumbEntry, Route } from "./routing";
import type { MeDTO } from "@shared/api";

type AuthState =
  | { status: "loading" }
  | { status: "signed-out" }
  | { status: "signed-in"; me: MeDTO };

export default function App() {
  const [auth, setAuth] = useState<AuthState>({ status: "loading" });
  const [route, setRoute] = useState<Route>("dashboard");
  const [subId, setSubId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [palette, setPalette] = usePalette();

  useEffect(() => {
    api.auth
      .me()
      .then((me) => setAuth({ status: "signed-in", me }))
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          setAuth({ status: "signed-out" });
        } else {
          console.error("auth.me failed", err);
          setAuth({ status: "signed-out" });
        }
      });
  }, []);

  const navTo = useCallback((r: Route) => {
    setRoute(r);
    setSubId(null);
    setSearch("");
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const openSub = useCallback((id: string) => {
    setSubId(id);
    setRoute("subscription");
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const onLogout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch (err) {
      console.error("logout failed", err);
    }
    setAuth({ status: "signed-out" });
    setRoute("dashboard");
    setSubId(null);
  }, []);

  if (auth.status === "loading") {
    return <div className="bbc-loading">Loading Barose Business Central</div>;
  }
  if (auth.status === "signed-out") {
    return (
      <>
        <Login
          onAuthenticated={(me) => {
            setAuth({ status: "signed-in", me });
            setRoute("dashboard");
            setSubId(null);
          }}
        />
        <TweaksPanel palette={palette} onPaletteChange={setPalette} />
      </>
    );
  }

  const me = auth.me;
  const crumbs: CrumbEntry[] = (() => {
    switch (route) {
      case "dashboard":
        return [{ label: "Dashboard" }];
      case "subscriptions":
        return [{ label: "Subscriptions" }];
      case "subscription":
        return [
          { label: "Subscriptions", route: "subscriptions" },
          { label: subId ?? "Subscription" },
        ];
      case "catalog":
        return [{ label: "Catalog" }];
      case "users":
        return [{ label: "Users & Seats" }];
    }
  })();

  const navActive: Route = route === "subscription" ? "subscriptions" : route;

  return (
    <div className="bbc-app">
      <Sidebar
        route={navActive}
        partnerName={me.partner.name}
        onNav={navTo}
        onSwitchPartner={onLogout}
      />
      <div className="bbc-main">
        <Topbar
          crumbs={crumbs}
          onNav={navTo}
          search={search}
          setSearch={setSearch}
          partnerName={me.partner.name}
          userName={me.user.name}
          userInitials={me.user.initials}
          userRole={me.user.role}
          onLogout={onLogout}
        />
        <main className="bbc-content">
          {route === "dashboard" && (
            <Dashboard me={me} onOpenSubscription={openSub} onNav={navTo} />
          )}
          {route === "subscriptions" && (
            <Subscriptions
              onOpenSubscription={openSub}
              onNav={navTo}
              search={search}
            />
          )}
          {route === "subscription" && subId && (
            <SubscriptionDetail
              key={subId}
              subId={subId}
              onNav={navTo}
              onOpenSubscription={openSub}
            />
          )}
          {route === "catalog" && (
            <Catalog search={search} onOpenSubscription={openSub} />
          )}
          {route === "users" && (
            <Users search={search} onOpenSubscription={openSub} />
          )}
        </main>
      </div>
      <TweaksPanel palette={palette} onPaletteChange={setPalette} />
    </div>
  );
}
